import { queryOptions, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { setSqlImportDatabaseCache } from './cache';
import { parseSqlImportDatabase, sqlImportDatabaseKeys, type SqlDatabaseType, type SqlImportDatabase } from './entity';

/**
 * The workspace's connected databases, optionally narrowed to one engine. With
 * a `type`, the backend also drops the `cio_draft` placeholders; without one it
 * returns every database, placeholders included.
 *
 * @public
 */
export async function getSqlImportDatabases(
  workspaceId: number,
  type: SqlDatabaseType | undefined,
  qc: QueryClient,
): Promise<SqlImportDatabase[]> {
  const data = await hydraUiApi.sqlImportDatabases.list({
    params: { path: { environment_id: String(workspaceId) }, query: type == null ? undefined : { type } },
  });
  const databases = data.sql_import_databases.map(parseSqlImportDatabase);
  for (const database of databases) {
    setSqlImportDatabaseCache(qc, workspaceId, database);
  }
  return databases;
}

/**
 * Always fresh: the create wizard lists the databases on every entry and adds
 * to them mid-flow, and a connection can be edited from another tab.
 *
 * @public
 */
export const getSqlImportDatabasesQueryOptions = (
  workspaceId: number,
  type: SqlDatabaseType | undefined,
  qc: QueryClient,
) =>
  queryOptions({
    queryKey: sqlImportDatabaseKeys.list(workspaceId, type),
    queryFn: () => getSqlImportDatabases(workspaceId, type, qc),
    staleTime: 0,
  });

interface UseSqlImportDatabasesOptions {
  /** Narrow to one engine (and drop draft placeholders). Omit for every database. */
  type?: SqlDatabaseType;
  /**
   * Use this id instead of reading the URL. Pages that render inside a Suspense
   * boundary can mount while the URL is still mid-redirect, so a caller that
   * already resolved the workspace passes it in.
   */
  workspaceId?: number;
  queryConfig?: QueryConfig<typeof getSqlImportDatabasesQueryOptions>;
}

/** @public */
export const useSqlImportDatabases = ({
  type,
  workspaceId: overrideWorkspaceId,
  queryConfig,
}: UseSqlImportDatabasesOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({ ...getSqlImportDatabasesQueryOptions(workspaceId, type, qc), ...queryConfig });
};
